import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";

const EMPTY = { name: "", address: "", reference: "", notes: "" };

export default function Commesse() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    try {
      const { data } = await api.get("/commesse");
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name || "", address: r.address || "",
      reference: r.reference || "", notes: r.notes || "",
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/commesse/${editing.id}`, form);
        toast.success("Commessa aggiornata");
      } else {
        await api.post("/commesse", form);
        toast.success("Commessa creata");
      }
      setShowForm(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Eliminare la commessa "${r.name}"?`)) return;
    try {
      await api.delete(`/commesse/${r.id}`);
      toast.success("Commessa eliminata");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <PageHeader
        title="Commesse"
        subtitle="Anagrafica delle commesse destinatarie degli scarichi dai furgoni tecnici"
        action={
          <button
            data-testid="new-commessa-btn"
            onClick={openNew}
            className="text-sm px-4 py-2.5 bg-[#002FA7] hover:bg-[#002480] text-white transition-colors duration-150 flex items-center gap-2"
          >
            <Plus size={16} weight="bold" /> Nuova Commessa
          </button>
        }
      />
      <div className="p-8">
        <div className="bg-white border border-neutral-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr className="text-left">
                <th className="px-4 py-3 label-eyebrow">Nome</th>
                <th className="px-4 py-3 label-eyebrow">Indirizzo</th>
                <th className="px-4 py-3 label-eyebrow">Riferimento</th>
                <th className="px-4 py-3 label-eyebrow text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan="4" className="px-4 py-12 text-center text-neutral-500">Nessuna commessa. Aggiungi la prima.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} className="border-b border-neutral-100 hover:bg-neutral-50 transition-colors">
                  <td className="px-4 py-3 font-medium">{r.name}</td>
                  <td className="px-4 py-3 text-neutral-600">{r.address || "—"}</td>
                  <td className="px-4 py-3 text-neutral-600">{r.reference || "—"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button data-testid={`edit-commessa-${r.id}`} onClick={() => openEdit(r)} className="p-1.5 hover:bg-neutral-200 transition-colors mr-1"><PencilSimple size={16} /></button>
                    <button data-testid={`delete-commessa-${r.id}`} onClick={() => remove(r)} className="p-1.5 hover:bg-red-100 text-red-600 transition-colors"><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-2 md:p-4">
          <form onSubmit={submit} className="bg-white border border-neutral-200 shadow-xl w-full max-w-lg max-h-[95vh] flex flex-col overflow-y-auto" data-testid="commessa-form">
            <div className="p-6 border-b border-neutral-200">
              <div className="label-eyebrow mb-1">{editing ? "Modifica" : "Nuova"}</div>
              <h2 className="font-display font-bold text-xl tracking-tight">Commessa</h2>
            </div>
            <div className="p-6 space-y-4">
              <label className="block">
                <span className="label-eyebrow block mb-2">Nome *</span>
                <input required data-testid="commessa-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </label>
              <label className="block">
                <span className="label-eyebrow block mb-2">Indirizzo</span>
                <input data-testid="commessa-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </label>
              <label className="block">
                <span className="label-eyebrow block mb-2">Codice / Riferimento commessa</span>
                <input data-testid="commessa-reference" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </label>
              <label className="block">
                <span className="label-eyebrow block mb-2">Note</span>
                <textarea data-testid="commessa-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </label>
            </div>
            <div className="p-6 pt-0 flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-neutral-300 hover:bg-neutral-100 transition-colors">Annulla</button>
              <button data-testid="commessa-submit" type="submit" className="px-4 py-2 text-sm bg-[#002FA7] hover:bg-[#002480] text-white transition-colors">{editing ? "Salva" : "Crea"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
